import { DataModelEntity } from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { CodeEditorSelection } from '@flowx/designer/modules/monaco-code-editor/types'
import { EditDiffPayload } from '@flowx/designer/modules/new-ai/agents/designer/services/designer-new.service'
import {
  AiLocationUI,
  AiTemplateConfig,
} from '@flowx/designer/modules/new-ai/agents/designer/services/designer.service'
import { TemplateConfigInterface } from '@flowx/shared/interfaces'

import { AiProcess, AiProcessEntities } from '../agents/analyst/services/analyst.service'
import { AiUiFlowResponse } from '../agents/designer/services/ui-flow-designer.service'
import {
  FixWarningsBusinessRuleResponse,
  GenerateBusinessRuleResponse,
  GenerateExpressionResponse,
} from '../agents/developer/api/developer.api'
import {
  AiAgent,
  AiPermissions,
  AiPlan,
  BusinessRuleContext,
  ContextDocument,
  DataModelContext,
  DataModelDraft,
  DesignerContext,
  DocumentContextReference,
  EnumerationContext,
  FileUploadConfig,
  InitialMessageContext,
  Message,
  MessageLog,
  NotificationContent,
  NotificationContext,
  Position,
  ReasoningLog,
  UiExpressionContext,
  UploadContextFile,
} from '../common/ai.dictionary'

export type AiState = {
  permissions: AiPermissions
  fabPosition: Position
  fileUploadConfig: FileUploadConfig | null
  businessRuleContext: BusinessRuleContext | null
  reusableFunctionContext: {
    name: string
    description: string
    language: string
    expression: string
    inputParamsType: string
    outputParamsType: string
    inputSlices: any[]
    outputSlices: any[]
  } | null
  expressionContext: UiExpressionContext | null
  codeSelectionContext: CodeEditorSelection
  minimized: boolean
  zenMode: boolean
  selectedAgent: AiAgent | null
  currentPlanStep: any | null
  selectedPlan: AiPlan | null
  generatedPrompt: any | null
  agentActions: string
  selectedDocument: ContextDocument | null
  selectedDocumentContext: DocumentContextReference | null
  selectedDesignerUserTaskAreaId: number | null
  selectedDesignerTemplateConfigId: number | null | null
  selectedDesignerNodeId: number | null
  sessionId: string | null
  // request_id of the most recent agent generation in this session.
  // Observatory groups runs by request_id; Keep / Discard send this to
  // /api/run/feedback to mark Run.saved or Run.discarded.
  lastRequestId: string | null
  isAiInteractionAllowed: boolean
  isFileUploadAllowed: boolean
  isChatConversationAllowed: boolean
  isStreaming: boolean
  messages: Message[]
  logs: MessageLog[]
  reasoningLogs: ReasoningLog[]
  generatedHTML: string | null
  uploadedFiles: UploadContextFile[]
  uploadingDocuments: UploadContextFile[]
  isPlanEnabled: boolean
  isUploadingDocuments: boolean
  isPlanSidebarVisible: boolean
  isChatSidebarVisible: boolean
  isDocumentsSidebarVisible: boolean
  isArchivedSidebarVisible: boolean
  pendingDocumentsLoading: boolean
  pendingAiResponse: boolean
  pendingFixWarningsResponse: boolean
  pendingFixErrorResponse: boolean
  pendingKeepDraftSave: boolean
  generatedAiRecommendation: string | null
  streamingAiResponse: boolean
  backgroundAiResponse: boolean
  hasAudit: boolean
  currentDocuments: ContextDocument[]

  draftMessage: InitialMessageContext | null
  initialMessage: Message | null
  suggestedPrompts: string[] | null

  generatedProcess: AiProcess | null
  temporaryProcess: AiProcess | null

  generatedProcessEntities: AiProcessEntities | null
  temporaryProcessEntities: AiProcessEntities | null

  generatedProcessPreviewHeader: {
    containerTransform?: string
    contentTransform?: string
  } | null
  startedFromSequence: number | null
  generatedSequenceProcess: AiProcess | null

  generatedUI: AiTemplateConfig | null
  temporaryUI: AiTemplateConfig | null
  generatedUINew: any | null
  generatedLocationId: AiLocationUI | null
  generatedEditDiff: EditDiffPayload | null
  editDiffAppliedConfig: TemplateConfigInterface | null
  generatedUIDataModelChanges: DataModelDraft | null
  generatedBusinessRule: GenerateBusinessRuleResponse | null
  generatedTestData: string | null
  generatedFixWarningsBusinessRule: FixWarningsBusinessRuleResponse | null
  generatedFixErrorBusinessRule: FixWarningsBusinessRuleResponse | null
  generatedExpression: GenerateExpressionResponse | null
  generatedGatewayCondition: any | null
  generatedImprovedCode: string | null
  generatedAddComment: string | null
  generatedCustomComponent: {
    sourceCode: string | null
    styles: string | null
    packageJson: string | null
  } | null
  customComponentContext: {
    sourceCode: string | null
    styles: string | null
    packageJson: string | null
    name: string
    uniqueIdentifier: string
  } | null

  currentEnumeration: EnumerationContext | null
  currentNotification: NotificationContext | null
  currentUI: DesignerContext | any | null
  currentFullUI: TemplateConfigInterface | null
  currentDataModel: DataModelEntity[] | null

  generatedEnumeration: EnumerationContext | null
  generatedNotification: NotificationContent | null
  generatedImage: string | null
  generatedDataModel: DataModelContext | null
  pendingRouteToAssistant: any
  pendingRouteToDesigner: any
  pendingRouteToAnalyst: any
  pendingRouteToDeveloper: any
  pendingRouteToCoordinator: any
  pendingRouteToArchitect: any
  pendingRouteToIntegrator: any
  pendingRouteToAuditor: any
  generatingUi: boolean
  generatingPlan: boolean
  currentAITaskMessage: string | null
  documentUploadMessage: string | null
  waitingForHumanApproval: boolean
  generatedIntent: any | null
  generatedActions: any[] | null
  autonomyLevel: number
  appId: string | null
  appName: string | null
  appVersionId: string | null
  processId: string | null
  processName: string | null
  process_warnings_no: number
  process_rules: any[]
  process_warnings: any[]
  currentPlans: any[] | null
  screen_context: any | null
  generatedAgentWorkflow: any | null
  generatedDatasource: any | null
  currentSystemContext: any | null
  designerVersion: 'v1' | 'v2'

  // UI Flow designer state
  generatedUiFlow: AiUiFlowResponse | null
  generatedUiFlowTemplateConfigs: TemplateConfigInterface[] | null
  selectedUiFlowSubtreeId: number | null
  selectedUiFlowPageIndex: number
}

export const initialState: AiState = {
  permissions: {
    showAssistantAgent: false,
    showAnalystAgent: false,
    showDesignerAgent: false,
    showDeveloperAgent: false,
    canViewDocuments: false,
    canUploadDocuments: false,
  },
  fabPosition: { right: 24, bottom: 80 },
  fileUploadConfig: null,
  businessRuleContext: null,
  reusableFunctionContext: null,
  expressionContext: null,
  codeSelectionContext: null,
  generatedPrompt: null,
  minimized: true,
  zenMode: false,
  selectedAgent: null,
  isPlanEnabled: false,
  currentPlanStep: null,
  selectedPlan: null,
  agentActions: '',
  selectedDocument: null,
  selectedDocumentContext: null,
  selectedDesignerUserTaskAreaId: null,
  selectedDesignerTemplateConfigId: null,
  selectedDesignerNodeId: null,
  sessionId: null,
  lastRequestId: null,
  isAiInteractionAllowed: true,
  isFileUploadAllowed: true,
  isChatConversationAllowed: true,
  isUploadingDocuments: false,

  isPlanSidebarVisible: false,
  isChatSidebarVisible: false,
  isDocumentsSidebarVisible: false,
  isArchivedSidebarVisible: false,
  messages: [],
  logs: [],
  reasoningLogs: [],
  uploadedFiles: [],
  uploadingDocuments: [],
  isStreaming: false,
  generatedHTML: null,
  pendingDocumentsLoading: false,
  pendingAiResponse: false,
  pendingFixWarningsResponse: false,
  pendingFixErrorResponse: false,
  pendingKeepDraftSave: false,
  generatedAiRecommendation: null,
  streamingAiResponse: false,
  backgroundAiResponse: false,
  currentDocuments: [],
  hasAudit: false,

  draftMessage: null,
  initialMessage: null,
  suggestedPrompts: null,

  generatedProcess: null,
  temporaryProcess: null,
  generatedProcessEntities: null,
  temporaryProcessEntities: null,
  startedFromSequence: null,
  generatedSequenceProcess: null,

  generatedProcessPreviewHeader: null,
  generatedUI: null,
  temporaryUI: null,
  generatedUINew: null,
  generatedLocationId: null,
  generatedEditDiff: null,
  editDiffAppliedConfig: null,
  generatedUIDataModelChanges: null,
  generatedBusinessRule: null,
  generatedTestData: null,
  generatedFixWarningsBusinessRule: null,
  generatedFixErrorBusinessRule: null,
  generatedExpression: null,
  generatedGatewayCondition: null,
  generatedImprovedCode: null,
  generatedAddComment: null,
  generatedCustomComponent: null,
  customComponentContext: null,

  currentUI: null,
  currentFullUI: null,
  currentEnumeration: null,
  currentNotification: null,
  currentDataModel: null,
  generatedEnumeration: null,
  generatedNotification: null,
  generatedImage: null,
  generatedDataModel: null,
  pendingRouteToAssistant: null,
  pendingRouteToDesigner: null,
  pendingRouteToAnalyst: null,
  pendingRouteToDeveloper: null,
  pendingRouteToCoordinator: null,
  pendingRouteToArchitect: null,
  pendingRouteToIntegrator: null,
  pendingRouteToAuditor: null,
  generatingUi: false,
  generatingPlan: false,
  currentAITaskMessage: null,
  documentUploadMessage: null,
  waitingForHumanApproval: false,
  generatedIntent: null,
  generatedActions: null,
  autonomyLevel: 0,
  appId: null,
  appVersionId: null,
  appName: null,
  processId: null,
  processName: null,
  process_warnings_no: 0,
  process_rules: [],
  process_warnings: [],
  currentPlans: null,
  screen_context: null,
  generatedAgentWorkflow: null,
  generatedDatasource: null,
  currentSystemContext: null,
  designerVersion: 'v2',

  // UI Flow designer state
  generatedUiFlow: null,
  generatedUiFlowTemplateConfigs: null,
  selectedUiFlowSubtreeId: null,
  selectedUiFlowPageIndex: 0,
}
